import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/index.jsx';
import Icon from '../components/Icon.jsx';
import Logo from '../components/Logo.jsx';
import { shortWhen } from './chatUtils.jsx';
import { usePresence } from './ChatProvider.jsx';
import { Dot } from './Presence.jsx';

// Left column: search, filter (All / Unread / Archived), conversations with
// logo, name, product chip, last message, time and unread count. Members
// see "Support PME" pinned at the top.
export function Avatar({ person, support, size = 44, status }) {
  let img;
  if (support) img = <span className="chat-avatar chat-avatar-pme" style={{ width: size, height: size }}><Logo size={Math.round(size * 0.7)} /></span>;
  else if (person?.logoUrl) img = <img className="chat-avatar" src={person.logoUrl} alt="" style={{ width: size, height: size }} />;
  else img = <span className="chat-avatar chat-avatar-empty" style={{ width: size, height: size }} aria-hidden="true">{(person?.company || person?.name || '?').trim().slice(0, 1).toUpperCase()}</span>;
  return <span className="chat-avatar-wrap">{img}<Dot status={status} /></span>;
}
export const convTitle = (c, t, team) => (c.kind === 'support' && !team ? t('chat.support') : c.other?.company || c.other?.name || t('chat.unknown'));

export default function ConversationList({ items, activeId, base, filter, setFilter, q, setQ, team, pinSupport, onSupport, loading }) {
  const { t, lang } = useI18n();
  const presence = usePresence((items || []).map((c) => c.other?.id));
  const support = pinSupport ? items?.find((c) => c.kind === 'support') : null;
  const rest = (items || []).filter((c) => c !== support);
  const row = (c) => {
    const title = convTitle(c, t, team);
    const preview = c.last ? `${c.last.mine ? `${t('chat.you')} : ` : ''}${c.last.kind === 'product' ? `🛍 ${t('chat.sharedProduct')}` : c.last.kind === 'quote' ? `🧾 ${t('chat.quote')}` : c.last.body || `📎 ${t('chat.attachment')}`}` : '';
    return (
      <li key={c.id}>
        <Link to={`${base}/${c.id}`} className={`chat-row${String(c.id) === String(activeId) ? ' is-active' : ''}${c.unread ? ' is-unread' : ''}`} aria-current={String(c.id) === String(activeId) ? 'true' : undefined}>
          <Avatar person={c.other} support={c.kind === 'support' && !team} status={c.other?.id ? presence[c.other.id] : null} />
          <span className="chat-row-main">
            <span className="chat-row-top">
              <strong className="chat-row-name">{title}</strong>
              <span className="chat-row-time">{shortWhen(c.lastAt, lang)}</span>
            </span>
            {team && c.kind === 'support' && <span className="chat-chip chat-chip-support">{t('chat.support')}</span>}
            {c.listing && <span className="chat-chip">🛍 {c.listing.title}</span>}
            {c.assignedTo && <span className="chat-chip chat-chip-rep" title={t('chat.handledBy')}>👤 {c.assignedTo.name}</span>}
            <span className="chat-row-bottom">
              <span className="chat-row-preview">{c.blockedByMe ? `⛔ ${t('chat.blockedShort')}` : preview}</span>
              {c.muted && <span className="chat-muted" title={t('chat.muted')} aria-label={t('chat.muted')}>🔕</span>}
              {c.unread > 0 && <span className="chat-unread" aria-label={t('chat.unreadN', { n: c.unread })}>{c.unread}</span>}
            </span>
          </span>
        </Link>
      </li>
    );
  };
  return (
    <div className="chat-list">
      <div className="chat-list-tools">
        <label className="chat-search"><Icon name="search" size={17} /><span className="sr-only">{t('chat.search')}</span>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('chat.search')} /></label>
        <div className="chat-filters" role="group" aria-label={t('chat.filter')}>
          {(team ? ['all', 'unread'] : ['all', 'unread', 'archived']).map((f) => <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>{t(`chat.filters.${f}`)}</button>)}
        </div>
      </div>
      <ul className="chat-rows">
        {pinSupport && filter !== 'archived' && !q && (support ? row(support) : (
          <li><button type="button" className="chat-row chat-row-support" onClick={onSupport}>
            <Avatar support />
            <span className="chat-row-main"><span className="chat-row-top"><strong className="chat-row-name">{t('chat.support')}</strong></span><span className="chat-row-preview">{t('chat.supportHint')}</span></span>
          </button></li>
        ))}
        {rest.map(row)}
        {!loading && items && !rest.length && <li className="chat-empty-list muted small">{filter === 'unread' ? t('chat.noUnread') : filter === 'archived' ? t('chat.noArchived') : q ? t('chat.noMatch') : t('chat.noConversations')}</li>}
      </ul>
    </div>
  );
}
